import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  KNOWLEDGE_ITEMS_COLLECTION,
  KNOWLEDGE_ITEM_TYPES,
} from './knowledgeItemModel';
import { isKnowledgeFactLifecycleVisible } from '../profileContract/profileKnowledgeProjection';

const TYPE_LABELS = {
  [KNOWLEDGE_ITEM_TYPES.PROFILE]: 'Profile',
  [KNOWLEDGE_ITEM_TYPES.RISK]: 'Risk',
  [KNOWLEDGE_ITEM_TYPES.TRIGGER]: 'Trigger',
  [KNOWLEDGE_ITEM_TYPES.STRATEGY]: 'Strategy',
  [KNOWLEDGE_ITEM_TYPES.SAFETY]: 'Safety',
  [KNOWLEDGE_ITEM_TYPES.FEEDING]: 'Feeding',
  [KNOWLEDGE_ITEM_TYPES.SLEEP]: 'Sleep',
  [KNOWLEDGE_ITEM_TYPES.MEDICATION]: 'Medications',
  [KNOWLEDGE_ITEM_TYPES.PREFERENCE]: 'Preference',
  [KNOWLEDGE_ITEM_TYPES.GOAL]: 'Goal',
  [KNOWLEDGE_ITEM_TYPES.INSTRUCTION]: 'Health & Care',
  [KNOWLEDGE_ITEM_TYPES.SUPPORT]: 'Support',
  [KNOWLEDGE_ITEM_TYPES.CONTACT]: 'Contact',
  [KNOWLEDGE_ITEM_TYPES.CONDITION]: 'Condition',
  [KNOWLEDGE_ITEM_TYPES.ROUTINE]: 'Routine',
  [KNOWLEDGE_ITEM_TYPES.BASELINE]: 'Baseline',
  [KNOWLEDGE_ITEM_TYPES.NOTE]: 'Note',
  [KNOWLEDGE_ITEM_TYPES.OTHER]: 'Other',
};

export const KNOWLEDGE_ITEM_TYPE_ORDER = [
  KNOWLEDGE_ITEM_TYPES.PROFILE,
  KNOWLEDGE_ITEM_TYPES.GOAL,
  KNOWLEDGE_ITEM_TYPES.STRATEGY,
  KNOWLEDGE_ITEM_TYPES.RISK,
  KNOWLEDGE_ITEM_TYPES.TRIGGER,
  KNOWLEDGE_ITEM_TYPES.SUPPORT,
  KNOWLEDGE_ITEM_TYPES.MEDICATION,
  KNOWLEDGE_ITEM_TYPES.SAFETY,
  KNOWLEDGE_ITEM_TYPES.FEEDING,
  KNOWLEDGE_ITEM_TYPES.SLEEP,
  KNOWLEDGE_ITEM_TYPES.PREFERENCE,
  KNOWLEDGE_ITEM_TYPES.ROUTINE,
  KNOWLEDGE_ITEM_TYPES.CONTACT,
  KNOWLEDGE_ITEM_TYPES.INSTRUCTION,
  KNOWLEDGE_ITEM_TYPES.CONDITION,
  KNOWLEDGE_ITEM_TYPES.BASELINE,
  KNOWLEDGE_ITEM_TYPES.NOTE,
  KNOWLEDGE_ITEM_TYPES.OTHER,
];

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return 0;
};

export const getKnowledgeItemTypeLabel = (knowledgeType = '') => (
  TYPE_LABELS[knowledgeType] || TYPE_LABELS[KNOWLEDGE_ITEM_TYPES.OTHER]
);

const FEEDING_KNOWLEDGE_PATTERN = /\b(?:feeding plan|preferred foods?|foods working on|food preferences?|safe foods?|accepted foods?|try foods?|food goal|feeding goal|meal strateg(?:y|ies)|feeding strateg(?:y|ies)|accept one bite|one bite|no pressure|offer preferred food|praise attempts)\b/i;

export const isFeedingKnowledgeItem = (item = {}) => {
  const text = [
    item.title,
    item.summary,
    item.content,
    item.sourceText,
    item.knowledgeType,
  ].filter(Boolean).join(' ');

  return FEEDING_KNOWLEDGE_PATTERN.test(text);
};

export const shouldShowKnowledgeItem = (item = {}) => (
  isKnowledgeFactLifecycleVisible(item)
);

const normalizeDisplayKnowledgeType = (item = {}) => {
  if (KNOWLEDGE_ITEM_TYPE_ORDER.includes(item.knowledgeType)) return item.knowledgeType;
  if (KNOWLEDGE_ITEM_TYPE_ORDER.includes(item.factType)) return item.factType;
  return KNOWLEDGE_ITEM_TYPES.OTHER;
};

export const normalizeSavedKnowledgeItem = (item = {}) => ({
  id: item.id || '',
  childId: item.childId || '',
  captureId: item.captureId || '',
  title: item.title || 'Care knowledge',
  summary: item.summary || item.content || item.sourceText || '',
  knowledgeType: normalizeDisplayKnowledgeType(item),
  factType: normalizeDisplayKnowledgeType({
    knowledgeType: item.factType,
    factType: item.knowledgeType,
  }),
  bucket: normalizeDisplayKnowledgeType({
    knowledgeType: item.bucket || item.knowledgeType,
    factType: item.knowledgeType,
  }),
  customFactType: item.customFactType || '',
  sourceText: item.sourceText || '',
  sourceRefs: Array.isArray(item.sourceRefs) ? item.sourceRefs : [],
  evidenceReview: item.evidenceReview || null,
  patternKey: item.patternKey || '',
  observationCount: Number(item.observationCount || 0),
  hasConflictingEvidence: item.hasConflictingEvidence === true,
  sourceProvenance: item.sourceProvenance && typeof item.sourceProvenance === 'object'
    ? item.sourceProvenance
    : {},
  profileVisibilityOverride: item.profileVisibilityOverride === 'hide' ? 'hide' : '',
  profilePlacementMode: item.profilePlacementMode || '',
  profileChapterId: item.profileChapterId || '',
  profileField: item.profileField || '',
  factStatus: item.factStatus === 'outdated' || item.status === 'outdated' ? 'outdated' : 'current',
  capturedAt: item.capturedAt || item.createdAt || null,
  needsReview: item.needsReview === true
    || item.reviewState === 'needs_review'
    || item.reviewStatus === 'needs_review',
  reviewState: item.reviewState || '',
  reviewStatus: item.reviewStatus || '',
  archived: item.archived === true || item.status === 'archived',
});

export const groupKnowledgeItemsByType = (items = []) => {
  const groups = KNOWLEDGE_ITEM_TYPE_ORDER.map((knowledgeType) => ({
    id: knowledgeType,
    label: getKnowledgeItemTypeLabel(knowledgeType),
    items: [],
  }));
  const groupMap = new Map(groups.map((group) => [group.id, group]));

  items
    .filter(shouldShowKnowledgeItem)
    .map(normalizeSavedKnowledgeItem)
    .sort((a, b) => toMillis(b.capturedAt) - toMillis(a.capturedAt))
    .forEach((item) => {
      const groupId = item.bucket || item.knowledgeType;
      const group = groupMap.get(groupId) || groupMap.get(KNOWLEDGE_ITEM_TYPES.OTHER);
      group.items.push(item);
    });

  return groups.filter((group) => group.items.length > 0);
};

export const subscribeToKnowledgeItems = ({
  childId = '',
  onItems = () => {},
  onError = () => {},
} = {}, deps = {}) => {
  const cleanChildId = String(childId || '').trim();
  if (!cleanChildId) {
    onItems([]);
    return () => {};
  }

  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const queryFn = deps.query || query;
  const whereFn = deps.where || where;
  const snapshotFn = deps.onSnapshot || onSnapshot;
  const knowledgeQuery = queryFn(
    collectionFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION),
    whereFn('childId', '==', cleanChildId)
  );

  return snapshotFn(
    knowledgeQuery,
    (snapshot) => {
      onItems(snapshot.docs
        .map((itemDoc) => normalizeSavedKnowledgeItem({
          id: itemDoc.id,
          ...itemDoc.data(),
        }))
        .filter(shouldShowKnowledgeItem));
    },
    onError
  );
};

export const loadKnowledgeItems = async (childId = '', deps = {}) => {
  const cleanChildId = String(childId || '').trim();
  if (!cleanChildId) return [];
  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const queryFn = deps.query || query;
  const whereFn = deps.where || where;
  const getDocuments = deps.getDocs || getDocs;
  const snapshot = await getDocuments(queryFn(
    collectionFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION),
    whereFn('childId', '==', cleanChildId)
  ));

  return snapshot.docs
    .map((itemDoc) => normalizeSavedKnowledgeItem({ id: itemDoc.id, ...itemDoc.data() }))
    .filter(shouldShowKnowledgeItem);
};
